/**
 * JS Ed25519 implementation backed by TweetNaCl.
 *
 * TweetNaCl exposes Ed25519 keys as raw byte arrays:
 * - `publicKey`: 32 bytes (raw curve point)
 * - `secretKey`: 64 bytes (private + public, as used by TweetNaCl)
 *
 * The rest of this project (and the JVM demo) uses X.509 SPKI bytes as the canonical public key encoding,
 * so this file wraps/unwraps the raw 32-byte key into the standard Ed25519 SPKI DER structure:
 *
 *   302a300506032b6570032100 || raw32
 *
 * This keeps `peerId = base64url(SPKI)` consistent across JVM and browser nodes.
 */
package com.oasisfeng.invaluable.identity

import org.khronos.webgl.Uint8Array

@JsModule("tweetnacl")
@JsNonModule
private external val nacl: dynamic

actual object Ed25519 {

    private val spkiPrefix: ByteArray = byteArrayOf(
        0x30.toByte(),
        0x2a.toByte(),
        0x30.toByte(),
        0x05.toByte(),
        0x06.toByte(),
        0x03.toByte(),
        0x2b.toByte(),
        0x65.toByte(),
        0x70.toByte(),
        0x03.toByte(),
        0x21.toByte(),
        0x00.toByte(),
    )

    actual fun generateKeyPair(): KeyPairBytes {
        val pair = nacl.sign.keyPair()
        val publicKeyRaw = (pair.publicKey as Uint8Array).toByteArray()
        val secretKey = (pair.secretKey as Uint8Array).toByteArray()
        val spki = spkiPrefix + publicKeyRaw
        return KeyPairBytes(publicKeySpki = spki, privateKeyBytes = secretKey)
    }

    actual fun sign(message: ByteArray, privateKeyBytes: ByteArray): ByteArray {
        val sig = nacl.sign.detached(message.asUint8Array(), privateKeyBytes.asUint8Array()) as Uint8Array
        return sig.toByteArray()
    }

    actual fun verify(message: ByteArray, signature: ByteArray, publicKeySpki: ByteArray): Boolean {
        val rawPublic = unwrapSpki(publicKeySpki)
        return (nacl.sign.detached.verify(
            message.asUint8Array(),
            signature.asUint8Array(),
            rawPublic.asUint8Array(),
        ) as Boolean)
    }

    /** The TweetNaCl 64-byte secretKey is `seed(32) || publicKey(32)`, so the seed is its first 32 bytes. */
    actual fun seed(privateKeyBytes: ByteArray): ByteArray {
        require(privateKeyBytes.size >= 32) { "TweetNaCl Ed25519 secretKey too short: ${privateKeyBytes.size}" }
        return privateKeyBytes.copyOfRange(0, 32)
    }

    private fun unwrapSpki(spki: ByteArray): ByteArray {
        if (spki.size != spkiPrefix.size + 32) error("Invalid SPKI length: ${spki.size}")
        for (i in spkiPrefix.indices) {
            if (spki[i] != spkiPrefix[i]) error("Invalid SPKI prefix")
        }
        return spki.copyOfRange(spkiPrefix.size, spki.size)
    }
}

private fun ByteArray.asUint8Array(): Uint8Array {
    val int8 = this.asDynamic()
    return Uint8Array(int8.buffer, int8.byteOffset as Int, this.size)
}

private fun Uint8Array.toByteArray(): ByteArray {
    val len = this.length
    val out = ByteArray(len)
    val dyn = this.asDynamic()
    for (i in 0 until len) {
        out[i] = (dyn[i] as Int).toByte()
    }
    return out
}
